'use client';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

import type { PaymentType } from '~context/paywall/types';
import type {
	PaymentReason,
	PaymentReasonType,
} from '~features/annals/enums/payment';
import annalsCheckout from '~features/annals/hooks/checkout';
import { usePromoEvent } from '~features/promoEvent';
import { useTranslations } from '~i18n/hooks';
import type {
	TShopItem,
	TShopPromoCodeResponse,
} from '~services/api/credits/types';

import Discount from '../../../../../public/assets/icons/discount.svg';
import {
	deleteDefaultPromocode,
	getDefaultPromocode,
} from '../../promocode-storage';
import { PromoCodeForm } from '../PromoCodeForm';
import { PromoCodeWrapper, TextPlaceholder } from './PromoCode.styles';

type TPromoCodeInputProps = {
	selectedItem?: TShopItem;
	paymentType: PaymentType;
	reason?: PaymentReason;
	reasonType?: PaymentReasonType;
	defaultPromocode?: string;
	onApplied: (data: TShopPromoCodeResponse | undefined) => void;
};

export const PromoCode = ({
	selectedItem,
	reason,
	reasonType,
	paymentType,
	defaultPromocode,
	onApplied,
}: TPromoCodeInputProps) => {
	const [initialPromocode] = useState(
		() => defaultPromocode ?? getDefaultPromocode()
	);
	const [opened, setOpened] = useState(!!initialPromocode);
	const t = useTranslations('Promocode');

	const toggleOpen = () => {
		if ('startViewTransition' in document) {
			document.startViewTransition(() => {
				flushSync(() => {
					setOpened(true);
				});
			});
		} else {
			setOpened(true);
		}
	};

	useEffect(() => {
		return () => onApplied(undefined);
		//eslint-disable-next-line
	}, []);

	const promoCodeApplied = annalsCheckout.usePromocodeApplied({
		reason,
		reasonType,
		type: paymentType,
	});

	const logData = useCallback(
		(data: TShopPromoCodeResponse | undefined) => {
			const extra = { promocodeId: data?.Id, Prices: data?.Prices };
			void promoCodeApplied(extra);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedItem?.sku]
	);

	const onPromoCodeApplied = useCallback(
		(data: TShopPromoCodeResponse | undefined) => {
			deleteDefaultPromocode();
			logData(data);
			onApplied(data);
		},
		[logData, onApplied]
	);

	const { active: promoEventActive } = usePromoEvent();

	return (
		<PromoCodeWrapper reducedTopMargin={promoEventActive}>
			{!opened ? (
				<TextPlaceholder size="S" onClick={toggleOpen}>
					<Discount />
					{promoEventActive
						? t('applyPromocodeButtonTitle')
						: t('promocodeInputQuestion')}
				</TextPlaceholder>
			) : (
				<PromoCodeForm
					sku={selectedItem?.sku ?? ''}
					onSuccess={onPromoCodeApplied}
					defaultPromocode={initialPromocode}
				/>
			)}
		</PromoCodeWrapper>
	);
};
